import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { getUserNameById } from "../utils/functions";
import { getService } from "../reducers/service";
import { serviceNames } from "../utils/constants";
import { notify } from "../reducers/NotifierReducer";
import PATDeleteButton from "./Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

const SelectFilterDialog = ({ open, onClose, screenName, onFilterChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const users = useSelector((state) => state.dictionary.users);
  const { userData } = useSelector((state) => state.user);
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [openFilterDeleteDialog, setOpenFilterDeleteDialog] = useState(false);

  const selectedFilterId = useRef(null);

  const fetchFilters = async () => {
    try {
      const service = getService(serviceNames.perFilters);
      const result = await service.find({
        query: { screenName: screenName },
      });
      setSavedFilters(result);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFilters();
      setSearchQuery("");
      setShowOnlyMine(false);
      setSelectedFilter(null);
    }
  }, [open]);

  const filteredFilters = useMemo(() => {
    return savedFilters.filter((filter) => {
      const matchesSearch = filter.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase() || "");

      const isMine = filter.createdBy === userData.user.ptsUserID;
      const isGlobal = filter.availableToAll === true;

      const matchesMine = showOnlyMine
        ? isMine && !filter.availableToAll
        : isMine || isGlobal;

      return matchesSearch && matchesMine;
    });
  }, [savedFilters, searchQuery, showOnlyMine, userData.user.ptsUserID]);

  const handleApplyFilter = () => {
    if (!selectedFilter) return;
    try {
      const parsedValues = JSON.parse(selectedFilter.filterValues);
      onFilterChange(parsedValues);
      onClose();
    } catch (e) {
      console.error("Failed to parse filter:", e);
      dispatch(notify("Invalid filter format", "error"));
    }
  };

  const handleOpenDeleteDialog = (e, filterId) => {
    e.stopPropagation();
    selectedFilterId.current = filterId;
    setOpenFilterDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
      if (selectedFilterId.current) {
        try {
          const patchData = {
            isDeleted: true,
            deletedBy: userData?.user?.ptsUserID,
            deletedAt: new Date().toISOString(),
          };
          const service = getService(serviceNames.perFilters);
          const result = await service.patch(selectedFilterId.current, patchData);
          if (result) {
            dispatch(notify("Filter deleted successfully", "success"));
            fetchFilters();
          }
        } catch (error) {
          dispatch(notify("Error deleting filter", "error"));
        }
      }
      setOpenFilterDeleteDialog(false);
      selectedFilterId.current = null;
    };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontSize: "20px" }}>Select Filter</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <TextField
          label="Search Filters"
          variant="outlined"
          size="small"
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
          sx={{ mt: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlyMine}
              onChange={(e) => setShowOnlyMine(e.target.checked)}
            />
          }
          label="My Filters"
        />

        {filteredFilters.length === 0 ? (
          <Typography color="gray">No saved filters found.</Typography>
        ) : (
          <TableContainer
            sx={{
              border: "1px solid #ccc",
              borderRadius: "6px",
              height: 300,
              overflowY: "auto",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Filter Name/Description</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Created Date/Time</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFilters.map((f) => (
                  <TableRow
                    key={f.id}
                    hover
                    selected={selectedFilter?.id === f.id}
                    onClick={() => setSelectedFilter(f)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{f.name}</TableCell>
                    <TableCell>{getUserNameById(users, f.createdBy)}</TableCell>
                    <TableCell>
                      {f.createdAt
                        ? moment(f.createdAt).format("MM/DD/YYYY hh:mm A")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {f.createdBy === userData.user.ptsUserID && (
                        <PATDeleteButton
                          tooltipText="Delete Filter"
                          onClick={(e) => handleOpenDeleteDialog(e, f.id)}
                          size="20px"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleApplyFilter}
          variant="contained"
          disabled={!selectedFilter}
        >
          Apply Filter
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: theme.palette.mode === "dark" ? "#fff" : "primary",
            borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
            "&:hover": {
              borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>

      <DeleteConfirmationDialog
        open={openFilterDeleteDialog}
        onClose={() => {
          setOpenFilterDeleteDialog(false);
          selectedFilterId.current = null;
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Filter"
        message="Are you sure you want to delete this filter?"
      />
    </Dialog>
  );
};

export default SelectFilterDialog;
